/*
 * English and British regnal years: the year of a monarch's reign, which dated Acts of
 * Parliament and legal records until 1963 ("13 Cha. 2", "41 Geo. 3").
 *
 * Sources, checked in October 2026:
 * - UK Parliament, "UK Parliament session citations" (api.parliament.uk/regnal-years,
 *   Open Parliament Licence), compiled by House of Commons librarians: the regnal years of
 *   every reign from George III (1760) to Charles III with their start and end dates.
 *   Every year it lists is checked against this module in the tests.
 * - Reigns before 1760: the table of regnal years in the New Zealand Law Foundation's
 *   style guide (Appendix 5, 2018), which reproduces the standard reckoning of C R
 *   Cheney, Handbook of Dates (Royal Historical Society, rev M Jones, 2000), cross-checked
 *   with Wikipedia's "Regnal years of English and British monarchs" and, for the Tudor
 *   reigns, tudorhistory.org's calendar. Where they disagree the choice is noted below.
 * - Calendar: the Calendar (New Style) Act 1750 (24 Geo. 2 c. 23): in England and her
 *   colonies Wednesday 2 September 1752 was followed by Thursday 14 September 1752, and
 *   the year began on 1 January from 1752 instead of on 25 March (Lady Day).
 *
 * Rules the module applies:
 * - A regnal year runs from the anniversary of accession to the day before the next;
 *   the last year of a reign ends on the last day of the reign. A successor's first year
 *   begins on the day of accession, so on a day when one reign ended and another began
 *   the new reign's year is returned.
 * - Dates before 14 September 1752 are Julian ("Old Style"), as England wrote them; every
 *   date from then on is Gregorian. The Julian accession dates of reigns before 1752 recur
 *   on the same Julian day each year, so George II's 11 June (Old Style) became 22 June
 *   after 1752 (Gregorian) and regnal year 26 ran 11 June 1752 to 21 June 1753.
 * - Charles II's years count from 30 January 1649, the day his father was executed,
 *   although he was restored only on 29 May 1660, so his first twelve years fall in the
 *   Commonwealth and Protectorate and 12 Cha. 2 (30 January 1660 to 29 January 1661) is the
 *   year of the Convention Parliament.
 * - John's years begin on Ascension Day, a moveable feast (27 May 1199, 18 May 1200, ...);
 *   it is computed from the Julian Easter.
 * - William and Mary's years run from 13 February 1689 and carry on, numbered the same way,
 *   under William III after Mary's death on 27 December 1694. Philip and Mary's years are
 *   cited in pairs: "1 & 2 Phil. & Mar." is the first year from 25 July 1554.
 * - Edward IV's numbering continues through Henry VI's brief readeption (9 October 1470 to
 *   11 April 1471), and Henry VI's own readeption year is noted, not numbered.
 */

import { t } from "@/lib/i18n/interpolate";

/* ----------------------------------------------------------------------------- dates */

export type Calendar = "julian" | "gregorian";

export interface CivilDate {
  year: number;
  month: number;
  day: number;
}

/** The first day of the Gregorian calendar in England: Thursday 14 September 1752. */
export const GREGORIAN_START: CivilDate = { year: 1752, month: 9, day: 14 };

/** The last day of the Julian calendar in England: Wednesday 2 September 1752. */
export const JULIAN_END: CivilDate = { year: 1752, month: 9, day: 2 };

function floorDiv(a: number, b: number): number {
  return Math.floor(a / b);
}

/** Julian Day Number of a date in the given calendar (an integer day count). */
export function toJdn(date: CivilDate, calendar: Calendar): number {
  const a = floorDiv(14 - date.month, 12);
  const y = date.year + 4800 - a;
  const m = date.month + 12 * a - 3;
  const days = date.day + floorDiv(153 * m + 2, 5) + 365 * y + floorDiv(y, 4);
  if (calendar === "gregorian") {
    return days - floorDiv(y, 100) + floorDiv(y, 400) - 32_045;
  }
  return days - 32_083;
}

/** The date in the given calendar for a Julian Day Number. */
export function fromJdn(jdn: number, calendar: Calendar): CivilDate {
  let f = jdn + 1401;
  if (calendar === "gregorian") {
    f += floorDiv(floorDiv(4 * jdn + 274_277, 146_097) * 3, 4) - 38;
  }
  const e = 4 * f + 3;
  const g = floorDiv(e % 1461, 4);
  const h = 5 * g + 2;
  const day = floorDiv(h % 153, 5) + 1;
  const month = ((floorDiv(h, 153) + 2) % 12) + 1;
  const year = floorDiv(e, 1461) - 4716 + floorDiv(12 + 2 - month, 12);
  return { year, month, day };
}

const GREGORIAN_START_JDN = toJdn(GREGORIAN_START, "gregorian");

/** Which calendar England used on a day: Julian before 14 September 1752. */
export function englishCalendar(jdn: number): Calendar {
  return jdn < GREGORIAN_START_JDN ? "julian" : "gregorian";
}

/** A day's date as England wrote it at the time. */
export function englishDate(jdn: number): CivilDate {
  return fromJdn(jdn, englishCalendar(jdn));
}

export function isValidCivilDate(date: CivilDate, calendar: Calendar): boolean {
  if (
    !(
      Number.isInteger(date.year) &&
      Number.isInteger(date.month) &&
      Number.isInteger(date.day)
    ) ||
    date.month < 1 ||
    date.month > 12 ||
    date.day < 1
  ) {
    return false;
  }
  const back = fromJdn(toJdn(date, calendar), calendar);
  return (
    back.year === date.year &&
    back.month === date.month &&
    back.day === date.day
  );
}

/**
 * Until 1751 the English legal year began on 25 March, so a Julian date from 1 January to
 * 24 March belongs to the year before its calendar year: 30 January 1661 is written
 * 30 January 1660/61. Returns that earlier year, or null when the date needs no dual year.
 */
export function oldStyleYear(
  date: CivilDate,
  calendar: Calendar
): number | null {
  if (calendar !== "julian" || date.year >= 1752) {
    return null;
  }
  const beforeLadyDay = date.month < 3 || (date.month === 3 && date.day < 25);
  return beforeLadyDay ? date.year - 1 : null;
}

/* ------------------------------------------------------------------ Julian Easter */

/** Ascension Day (Easter + 39 days) of a year in the Julian calendar, as a day number. */
export function julianAscensionJdn(year: number): number {
  const a = year % 4;
  const b = year % 7;
  const c = year % 19;
  const d = (19 * c + 15) % 30;
  const e = (2 * a + 4 * b - d + 34) % 7;
  const sum = d + e + 114;
  const easter = toJdn(
    { year, month: floorDiv(sum, 31), day: (sum % 31) + 1 },
    "julian"
  );
  return easter + 39;
}

/* ------------------------------------------------------------------------- reigns */

export interface Reign {
  id: string;
  /** "Charles II" */
  name: string;
  /** The abbreviation legislation.gov.uk and the UK Parliament use: "Cha. 2". */
  abbr: string;
  /** Forms people type, written as `normalize` writes them. */
  aliases: readonly string[];
  /** The day regnal year 1 begins; later years begin on its anniversary. */
  base: CivilDate;
  /** The first and last day this entry covers. */
  from: CivilDate;
  to: CivilDate | null;
  /** Philip and Mary are cited in pairs: "1 & 2 Phil. & Mar.". */
  pairs?: boolean;
  /** Years begin on Ascension Day (John). */
  ascension?: boolean;
  /** The day the monarch was in fact restored, when the years count from earlier. */
  deFactoFrom?: CivilDate;
}

function d(year: number, month: number, day: number): CivilDate {
  return { year, month, day };
}

export const REIGNS: readonly Reign[] = [
  {
    id: "william1",
    name: "William I",
    abbr: "Will. 1",
    aliases: ["will 1", "william 1"],
    base: d(1066, 10, 14),
    from: d(1066, 10, 14),
    to: d(1087, 9, 9),
  },
  {
    id: "william2",
    name: "William II",
    abbr: "Will. 2",
    aliases: ["will 2", "william 2"],
    base: d(1087, 9, 26),
    from: d(1087, 9, 26),
    to: d(1100, 8, 2),
  },
  {
    id: "henry1",
    name: "Henry I",
    abbr: "Hen. 1",
    aliases: ["hen 1", "henry 1"],
    base: d(1100, 8, 5),
    from: d(1100, 8, 5),
    to: d(1135, 12, 1),
  },
  {
    id: "stephen",
    name: "Stephen",
    abbr: "Steph.",
    aliases: ["steph", "stephen", "ste"],
    base: d(1135, 12, 26),
    from: d(1135, 12, 26),
    to: d(1154, 10, 25),
  },
  {
    id: "henry2",
    name: "Henry II",
    abbr: "Hen. 2",
    aliases: ["hen 2", "henry 2"],
    base: d(1154, 12, 19),
    from: d(1154, 12, 19),
    to: d(1189, 7, 6),
  },
  {
    id: "richard1",
    name: "Richard I",
    abbr: "Ric. 1",
    aliases: ["ric 1", "richard 1"],
    base: d(1189, 9, 3),
    from: d(1189, 9, 3),
    to: d(1199, 4, 6),
  },
  {
    id: "john",
    name: "John",
    abbr: "John",
    aliases: ["john", "joh"],
    base: d(1199, 5, 27),
    from: d(1199, 5, 27),
    to: d(1216, 10, 19),
    ascension: true,
  },
  {
    id: "henry3",
    name: "Henry III",
    abbr: "Hen. 3",
    aliases: ["hen 3", "henry 3"],
    base: d(1216, 10, 28),
    from: d(1216, 10, 28),
    to: d(1272, 11, 16),
  },
  {
    id: "edward1",
    name: "Edward I",
    abbr: "Edw. 1",
    aliases: ["edw 1", "edward 1"],
    base: d(1272, 11, 20),
    from: d(1272, 11, 20),
    to: d(1307, 7, 7),
  },
  {
    id: "edward2",
    name: "Edward II",
    abbr: "Edw. 2",
    aliases: ["edw 2", "edward 2"],
    base: d(1307, 7, 8),
    from: d(1307, 7, 8),
    to: d(1327, 1, 20),
  },
  {
    id: "edward3",
    name: "Edward III",
    abbr: "Edw. 3",
    aliases: ["edw 3", "edward 3"],
    base: d(1327, 1, 25),
    from: d(1327, 1, 25),
    to: d(1377, 6, 21),
  },
  {
    id: "richard2",
    name: "Richard II",
    abbr: "Ric. 2",
    aliases: ["ric 2", "richard 2"],
    base: d(1377, 6, 22),
    from: d(1377, 6, 22),
    to: d(1399, 9, 29),
  },
  {
    id: "henry4",
    name: "Henry IV",
    abbr: "Hen. 4",
    aliases: ["hen 4", "henry 4"],
    base: d(1399, 9, 30),
    from: d(1399, 9, 30),
    to: d(1413, 3, 20),
  },
  {
    id: "henry5",
    name: "Henry V",
    abbr: "Hen. 5",
    aliases: ["hen 5", "henry 5"],
    base: d(1413, 3, 21),
    from: d(1413, 3, 21),
    to: d(1422, 8, 31),
  },
  {
    id: "henry6",
    name: "Henry VI",
    abbr: "Hen. 6",
    aliases: ["hen 6", "henry 6"],
    base: d(1422, 9, 1),
    from: d(1422, 9, 1),
    to: d(1461, 3, 4),
  },
  {
    id: "edward4",
    name: "Edward IV",
    abbr: "Edw. 4",
    aliases: ["edw 4", "edward 4"],
    base: d(1461, 3, 4),
    from: d(1461, 3, 4),
    to: d(1483, 4, 9),
  },
  {
    id: "edward5",
    name: "Edward V",
    abbr: "Edw. 5",
    aliases: ["edw 5", "edward 5"],
    base: d(1483, 4, 9),
    from: d(1483, 4, 9),
    to: d(1483, 6, 25),
  },
  {
    id: "richard3",
    name: "Richard III",
    abbr: "Ric. 3",
    aliases: ["ric 3", "richard 3"],
    base: d(1483, 6, 26),
    from: d(1483, 6, 26),
    to: d(1485, 8, 22),
  },
  {
    id: "henry7",
    name: "Henry VII",
    abbr: "Hen. 7",
    aliases: ["hen 7", "henry 7"],
    base: d(1485, 8, 22),
    from: d(1485, 8, 22),
    to: d(1509, 4, 21),
  },
  {
    id: "henry8",
    name: "Henry VIII",
    abbr: "Hen. 8",
    aliases: ["hen 8", "henry 8"],
    base: d(1509, 4, 22),
    from: d(1509, 4, 22),
    to: d(1547, 1, 28),
  },
  {
    id: "edward6",
    name: "Edward VI",
    abbr: "Edw. 6",
    aliases: ["edw 6", "edward 6"],
    base: d(1547, 1, 28),
    from: d(1547, 1, 28),
    to: d(1553, 7, 6),
  },
  {
    id: "mary1",
    name: "Mary I",
    abbr: "Mar.",
    aliases: ["mar", "mary", "mar 1", "mary 1"],
    base: d(1553, 7, 6),
    from: d(1553, 7, 6),
    to: d(1554, 7, 24),
  },
  {
    id: "philipmary",
    name: "Philip and Mary",
    abbr: "Phil. & Mar.",
    aliases: [
      "phil and mar",
      "ph and m",
      "ph and mar",
      "phil and m",
      "philip and mary",
    ],
    base: d(1554, 7, 25),
    from: d(1554, 7, 25),
    to: d(1558, 11, 17),
    pairs: true,
  },
  {
    id: "elizabeth1",
    name: "Elizabeth I",
    abbr: "Eliz. 1",
    aliases: ["eliz 1", "elizabeth 1"],
    base: d(1558, 11, 17),
    from: d(1558, 11, 17),
    to: d(1603, 3, 24),
  },
  {
    id: "james1",
    name: "James I",
    abbr: "Jac. 1",
    aliases: ["jac 1", "james 1", "jas 1"],
    base: d(1603, 3, 24),
    from: d(1603, 3, 24),
    to: d(1625, 3, 27),
  },
  {
    id: "charles1",
    name: "Charles I",
    abbr: "Cha. 1",
    aliases: ["cha 1", "car 1", "chas 1", "charles 1"],
    base: d(1625, 3, 27),
    from: d(1625, 3, 27),
    to: d(1649, 1, 30),
  },
  {
    id: "charles2",
    name: "Charles II",
    abbr: "Cha. 2",
    aliases: ["cha 2", "car 2", "chas 2", "charles 2"],
    base: d(1649, 1, 30),
    from: d(1649, 1, 30),
    to: d(1685, 2, 6),
    deFactoFrom: d(1660, 5, 29),
  },
  {
    id: "james2",
    name: "James II",
    abbr: "Jac. 2",
    aliases: ["jac 2", "james 2", "jas 2"],
    base: d(1685, 2, 6),
    from: d(1685, 2, 6),
    to: d(1688, 12, 11),
  },
  {
    id: "williammary",
    name: "William and Mary",
    abbr: "Will. & Mar.",
    aliases: ["will and mar", "william and mary", "w and m", "will and m"],
    base: d(1689, 2, 13),
    from: d(1689, 2, 13),
    to: d(1694, 12, 27),
  },
  {
    id: "william3",
    name: "William III",
    abbr: "Will. 3",
    aliases: ["will 3", "william 3"],
    base: d(1689, 2, 13),
    from: d(1694, 12, 28),
    to: d(1702, 3, 8),
  },
  {
    id: "anne",
    name: "Anne",
    abbr: "Ann.",
    aliases: ["ann", "anne", "ann 1", "anne 1"],
    base: d(1702, 3, 8),
    from: d(1702, 3, 8),
    to: d(1714, 8, 1),
  },
  {
    id: "george1",
    name: "George I",
    abbr: "Geo. 1",
    aliases: ["geo 1", "george 1"],
    base: d(1714, 8, 1),
    from: d(1714, 8, 1),
    to: d(1727, 6, 11),
  },
  {
    id: "george2",
    name: "George II",
    abbr: "Geo. 2",
    aliases: ["geo 2", "george 2"],
    base: d(1727, 6, 11),
    from: d(1727, 6, 11),
    to: d(1760, 10, 25),
  },
  {
    id: "george3",
    name: "George III",
    abbr: "Geo. 3",
    aliases: ["geo 3", "george 3"],
    base: d(1760, 10, 25),
    from: d(1760, 10, 25),
    to: d(1820, 1, 29),
  },
  {
    id: "george4",
    name: "George IV",
    abbr: "Geo. 4",
    aliases: ["geo 4", "george 4"],
    base: d(1820, 1, 29),
    from: d(1820, 1, 29),
    to: d(1830, 6, 26),
  },
  {
    id: "william4",
    name: "William IV",
    abbr: "Will. 4",
    aliases: ["will 4", "william 4"],
    base: d(1830, 6, 26),
    from: d(1830, 6, 26),
    to: d(1837, 6, 20),
  },
  {
    id: "victoria",
    name: "Victoria",
    abbr: "Vict.",
    aliases: ["vict", "victoria", "vic"],
    base: d(1837, 6, 20),
    from: d(1837, 6, 20),
    to: d(1901, 1, 22),
  },
  {
    id: "edward7",
    name: "Edward VII",
    abbr: "Edw. 7",
    aliases: ["edw 7", "edward 7"],
    base: d(1901, 1, 22),
    from: d(1901, 1, 22),
    to: d(1910, 5, 6),
  },
  {
    id: "george5",
    name: "George V",
    abbr: "Geo. 5",
    aliases: ["geo 5", "george 5"],
    base: d(1910, 5, 6),
    from: d(1910, 5, 6),
    to: d(1936, 1, 20),
  },
  {
    id: "edward8",
    name: "Edward VIII",
    abbr: "Edw. 8",
    aliases: ["edw 8", "edward 8"],
    base: d(1936, 1, 20),
    from: d(1936, 1, 20),
    to: d(1936, 12, 11),
  },
  {
    id: "george6",
    name: "George VI",
    abbr: "Geo. 6",
    aliases: ["geo 6", "george 6"],
    base: d(1936, 12, 11),
    from: d(1936, 12, 11),
    to: d(1952, 2, 6),
  },
  {
    id: "elizabeth2",
    name: "Elizabeth II",
    abbr: "Eliz. 2",
    aliases: ["eliz 2", "elizabeth 2"],
    base: d(1952, 2, 6),
    from: d(1952, 2, 6),
    to: d(2022, 9, 8),
  },
  {
    id: "charles3",
    name: "Charles III",
    abbr: "Chas. 3",
    aliases: ["chas 3", "cha 3", "car 3", "charles 3"],
    base: d(2022, 9, 8),
    from: d(2022, 9, 8),
    to: null,
  },
];

export function reignById(id: string): Reign | undefined {
  return REIGNS.find((reign) => reign.id === id);
}

function legalJdn(date: CivilDate): number {
  const julian = date.year < 1752 || (date.year === 1752 && date.month < 9);
  return toJdn(date, julian ? "julian" : "gregorian");
}

function baseCalendar(reign: Reign): Calendar {
  return legalJdn(reign.base) < GREGORIAN_START_JDN ? "julian" : "gregorian";
}

/** The day regnal year `number` of a reign begins, before the reign's own limits apply. */
function anniversaryJdn(reign: Reign, number: number): number {
  if (reign.ascension) {
    return julianAscensionJdn(reign.base.year + number - 1);
  }
  return toJdn(
    {
      year: reign.base.year + number - 1,
      month: reign.base.month,
      day: reign.base.day,
    },
    baseCalendar(reign)
  );
}

const FAR_FUTURE_JDN = toJdn({ year: 9999, month: 12, day: 31 }, "gregorian");

function fromJdnOf(reign: Reign): number {
  return legalJdn(reign.from);
}

function toJdnOf(reign: Reign): number {
  return reign.to ? legalJdn(reign.to) : FAR_FUTURE_JDN;
}

/** The regnal year, counted from the base, that a day falls in. */
function yearNumberAt(reign: Reign, jdn: number): number {
  let number = Math.max(1, englishDate(jdn).year - reign.base.year + 1);
  while (anniversaryJdn(reign, number + 1) <= jdn) {
    number += 1;
  }
  while (number > 1 && anniversaryJdn(reign, number) > jdn) {
    number -= 1;
  }
  return number;
}

export interface RegnalYear {
  reign: Reign;
  /** The number counted from the reign's base ("13" in 13 Cha. 2). */
  number: number;
  /** Day numbers of the first and last day of the year. */
  start: number;
  end: number;
  /** "13 Cha. 2", "1 & 2 Phil. & Mar." */
  label: string;
  /** The same without full stops, for OSCOLA: "13 Cha 2". */
  plainLabel: string;
  /** The year does not run its full length because the reign began or ended inside it. */
  partial: boolean;
}

function labelFor(reign: Reign, number: number): string {
  return reign.pairs
    ? `${number} & ${number + 1} ${reign.abbr}`
    : `${number} ${reign.abbr}`;
}

const RE_FULL_STOPS = /\./g;

/** The first and last day a reign entry covers (the last is null for the present reign). */
export function reignSpan(reign: Reign): { start: number; end: number | null } {
  return { start: fromJdnOf(reign), end: reign.to ? toJdnOf(reign) : null };
}

/** The numbers a reign's years are cited by, first and last. */
export function reignYearRange(reign: Reign): { first: number; last: number } {
  const first = yearNumberAt(reign, fromJdnOf(reign));
  // The current reign has no end: allow a hundred years.
  return {
    first,
    last: reign.to ? yearNumberAt(reign, toJdnOf(reign)) : first + 99,
  };
}

/** One regnal year of a reign, or null when the reign had no such year. */
export function regnalYear(reign: Reign, number: number): RegnalYear | null {
  const { first, last } = reignYearRange(reign);
  if (!Number.isInteger(number) || number < first || number > last) {
    return null;
  }
  const naturalStart = anniversaryJdn(reign, number);
  const naturalEnd = anniversaryJdn(reign, number + 1) - 1;
  const start = Math.max(naturalStart, fromJdnOf(reign));
  const end = Math.min(naturalEnd, toJdnOf(reign));
  const label = labelFor(reign, number);
  return {
    reign,
    number,
    start,
    end,
    label,
    plainLabel: label.replace(RE_FULL_STOPS, ""),
    partial: start !== naturalStart || end !== naturalEnd,
  };
}

/* ------------------------------------------------------------------------ lookup */

export type YearNote =
  | "janeGrey"
  | "readeption"
  | "commonwealth"
  | "sharedDay"
  | "edwardIiiFrance"
  | "earlyReign"
  | "ascension"
  | "williamIii"
  | "philipMary";

export type FindResult =
  | { kind: "year"; year: RegnalYear; notes: YearNote[] }
  | { kind: "commonwealth"; year: RegnalYear; notes: YearNote[] }
  | { kind: "interregnum"; notes: YearNote[] }
  | { kind: "before" };

const FIRST_JDN = legalJdn(REIGNS[0].from);
const JANE_GREY: [number, number] = [
  legalJdn(d(1553, 7, 10)),
  legalJdn(d(1553, 7, 19)),
];
const READEPTION: [number, number] = [
  legalJdn(d(1470, 10, 9)),
  legalJdn(d(1471, 4, 11)),
];
const EDWARD_III_FRANCE = legalJdn(d(1340, 1, 25));
const HENRY_III_START = legalJdn(d(1216, 10, 28));

function between(jdn: number, range: [number, number]): boolean {
  return jdn >= range[0] && jdn <= range[1];
}

function reignAt(jdn: number): Reign | null {
  let found: Reign | null = null;
  for (const reign of REIGNS) {
    if (fromJdnOf(reign) <= jdn && jdn <= toJdnOf(reign)) {
      found = reign;
    }
  }
  return found;
}

function notesFor(reign: Reign, jdn: number): YearNote[] {
  const notes: YearNote[] = [];
  if (between(jdn, JANE_GREY)) {
    notes.push("janeGrey");
  }
  if (between(jdn, READEPTION)) {
    notes.push("readeption");
  }
  if (reign.id === "edward3" && jdn >= EDWARD_III_FRANCE) {
    notes.push("edwardIiiFrance");
  }
  if (jdn < HENRY_III_START) {
    notes.push("earlyReign");
  }
  if (reign.ascension) {
    notes.push("ascension");
  }
  if (reign.id === "william3") {
    notes.push("williamIii");
  }
  if (reign.pairs) {
    notes.push("philipMary");
  }
  if (REIGNS.some((other) => other !== reign && toJdnOf(other) === jdn)) {
    notes.push("sharedDay");
  }
  return notes;
}

/** The regnal year a day falls in, with the cautions that belong to that date. */
export function findRegnalYear(jdn: number): FindResult {
  if (jdn < FIRST_JDN) {
    return { kind: "before" };
  }
  const reign = reignAt(jdn);
  if (!reign) {
    return { kind: "interregnum", notes: [] };
  }
  const number = yearNumberAt(reign, jdn);
  const year = regnalYear(reign, number);
  if (!year) {
    return { kind: "interregnum", notes: [] };
  }
  const notes = notesFor(reign, jdn);
  if (reign.deFactoFrom && jdn < legalJdn(reign.deFactoFrom)) {
    return { kind: "commonwealth", year, notes: ["commonwealth", ...notes] };
  }
  return { kind: "year", year, notes };
}

/* ---------------------------------------------------------------------- parsing */

const ROMAN_NUMERALS: Record<string, number> = {
  i: 1,
  ii: 2,
  iii: 3,
  iv: 4,
  v: 5,
  vi: 6,
  vii: 7,
  viii: 8,
};

const RE_PUNCTUATION = /[.,]/g;
const RE_AMPERSAND = /\s*&\s*/g;
const RE_SPACES = /\s+/g;
const RE_CITATION = /^(\d{1,3})(?:\s*(?:&|and|-|–)\s*(\d{1,3}))?\s+(\D.*)$/i;

/** "Charles II." → "charles 2"; "Ph. & M." → "ph and m". */
export function normalizeReignName(text: string): string {
  const tokens = text
    .toLowerCase()
    .replace(RE_PUNCTUATION, " ")
    .replace(RE_AMPERSAND, " and ")
    .replace(RE_SPACES, " ")
    .trim()
    .split(" ");
  const last = tokens.at(-1) ?? "";
  if (tokens.length > 1 && last in ROMAN_NUMERALS) {
    tokens[tokens.length - 1] = String(ROMAN_NUMERALS[last]);
  }
  return tokens.join(" ");
}

export function findReignByName(text: string): Reign | undefined {
  const name = normalizeReignName(text);
  return REIGNS.find(
    (reign) =>
      reign.aliases.includes(name) ||
      normalizeReignName(reign.abbr) === name ||
      normalizeReignName(reign.name) === name
  );
}

export type CitationParse =
  | { ok: true; reign: Reign; first: number; last: number }
  | { ok: false; reason: "format" }
  | { ok: false; reason: "reign" }
  | { ok: false; reason: "range"; reign: Reign };

/**
 * Read "13 Cha. 2", "41 Geo 3", "13 & 14 Cha. 2" (a session across two years),
 * "1 & 2 Phil. & Mar." and "1 Will. & Mar.". For a reign whose years are paired the two
 * numbers name one year. A year the reign never had is reported, not rounded.
 */
export function parseRegnalCitation(input: string): CitationParse {
  const match = RE_CITATION.exec(input.trim());
  if (!match) {
    return { ok: false, reason: "format" };
  }
  const reign = findReignByName(match[3]);
  if (!reign) {
    return { ok: false, reason: "reign" };
  }
  const first = Number(match[1]);
  const second = match[2] ? Number(match[2]) : first;
  const last = reign.pairs ? first : second;
  const { first: minimum, last: maximum } = reignYearRange(reign);
  if (
    last < first ||
    first < minimum ||
    last > maximum ||
    (reign.pairs && match[2] !== undefined && second !== first + 1)
  ) {
    return { ok: false, reason: "range", reign };
  }
  return { ok: true, reign, first, last };
}

const RE_ISO = /^(\d{1,4})-(\d{1,2})-(\d{1,2})$/;
const RE_SLASH = /^(\d{1,2})[/.](\d{1,2})[/.](\d{1,4})$/;
const RE_WORDS =
  /^(\d{1,2})(?:st|nd|rd|th)?\s+([A-Za-z]+)\.?,?\s+(\d{1,4})(?:\/(\d{1,4}))?$/;
const MONTH_NAMES = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
];

function monthFromName(name: string): number | null {
  const lower = name.toLowerCase();
  if (lower.length < 3) {
    return null;
  }
  const index = MONTH_NAMES.findIndex((month) => month.startsWith(lower));
  return index < 0 ? null : index + 1;
}

export type DateParse =
  | { ok: true; date: CivilDate; dualYear: boolean }
  | { ok: false };

/**
 * Read a date as people write it: 1661-01-30, 30/1/1661, 30 January 1661 or the
 * dual-year form 30 January 1660/61, which names January 1661 in the old legal year.
 */
export function parseDateInput(input: string): DateParse {
  const text = input.trim();
  const iso = RE_ISO.exec(text);
  if (iso) {
    return {
      ok: true,
      date: {
        year: Number(iso[1]),
        month: Number(iso[2]),
        day: Number(iso[3]),
      },
      dualYear: false,
    };
  }
  const slash = RE_SLASH.exec(text);
  if (slash) {
    return {
      ok: true,
      date: {
        year: Number(slash[3]),
        month: Number(slash[2]),
        day: Number(slash[1]),
      },
      dualYear: false,
    };
  }
  const words = RE_WORDS.exec(text);
  if (!words) {
    return { ok: false };
  }
  const month = monthFromName(words[2]);
  if (month === null) {
    return { ok: false };
  }
  const year = Number(words[3]);
  if (words[4] === undefined) {
    return {
      ok: true,
      date: { year, month, day: Number(words[1]) },
      dualYear: false,
    };
  }
  // "1660/61" or "1660/1": the second year is the calendar year of a January–March date.
  return {
    ok: true,
    date: { year: year + 1, month, day: Number(words[1]) },
    dualYear: true,
  };
}

export type DateResolve =
  | { ok: true; jdn: number; calendar: Calendar }
  | { ok: false; reason: "invalid" | "lost" | "dual" };

/**
 * Turn a parsed date into a day number. In "england" mode a date before 14 September 1752
 * is Julian and one after is Gregorian; the eleven days from 3 to 13 September 1752 did not
 * exist. In "gregorian" mode every date is Gregorian.
 */
export function resolveDate(
  parsed: { date: CivilDate; dualYear: boolean },
  mode: "england" | "gregorian"
): DateResolve {
  const { date } = parsed;
  if (mode === "gregorian") {
    if (parsed.dualYear || !isValidCivilDate(date, "gregorian")) {
      return { ok: false, reason: parsed.dualYear ? "dual" : "invalid" };
    }
    return { ok: true, jdn: toJdn(date, "gregorian"), calendar: "gregorian" };
  }
  const key = date.year * 10_000 + date.month * 100 + date.day;
  const lostFrom = 17_520_903;
  const lostTo = 17_520_913;
  if (key >= lostFrom && key <= lostTo) {
    return { ok: false, reason: "lost" };
  }
  const calendar: Calendar = key < lostFrom ? "julian" : "gregorian";
  if (!isValidCivilDate(date, calendar)) {
    return { ok: false, reason: "invalid" };
  }
  const beforeLadyDay = date.month < 3 || (date.month === 3 && date.day < 25);
  if (
    parsed.dualYear &&
    (calendar !== "julian" || !beforeLadyDay || date.year >= 1752)
  ) {
    return { ok: false, reason: "dual" };
  }
  return { ok: true, jdn: toJdn(date, calendar), calendar };
}

/* ---------------------------------------------------------------------- display */

/** The words and patterns a language wraps around a date; the widget supplies them. */
export interface DateFormat {
  /** January to December. */
  months: readonly string[];
  /** "{day} {month} {year}" */
  pattern: string;
  /** "{date} (Old Style)" for a Julian date. */
  oldStyle: string;
  /** "{written} = {gregorian}" when the two calendars differ. */
  equals: string;
}

export interface FormattedDay {
  /** The date as England wrote it, with a dual year for January to March before 1752. */
  written: string;
  /** The same day in the Gregorian calendar, when England then used the Julian. */
  gregorian: string | null;
  julian: boolean;
  /** Both together: "30 January 1660/61 (Old Style) = 9 February 1661". */
  full: string;
}

function dateText(date: CivilDate, year: string, format: DateFormat): string {
  return t(format.pattern, {
    day: String(date.day),
    month: format.months[date.month - 1] ?? "",
    year,
  });
}

/** "1660/61", "1699/1700": the old legal year and the calendar year it ends in. */
function dualYearText(earlier: number, later: number): string {
  const tail =
    later % 100 === 0 ? String(later) : String(later % 100).padStart(2, "0");
  return `${earlier}/${tail}`;
}

/** A day as England wrote it, and as the Gregorian calendar writes it. */
export function formatLegalDay(jdn: number, format: DateFormat): FormattedDay {
  const calendar = englishCalendar(jdn);
  const date = englishDate(jdn);
  const earlier = oldStyleYear(date, calendar);
  const yearText =
    earlier === null ? String(date.year) : dualYearText(earlier, date.year);
  const written = dateText(date, yearText, format);
  if (calendar === "gregorian") {
    return { written, gregorian: null, julian: false, full: written };
  }
  const gregorianDate = fromJdn(jdn, "gregorian");
  const gregorian = dateText(gregorianDate, String(gregorianDate.year), format);
  const marked = t(format.oldStyle, { date: written });
  return {
    written,
    gregorian,
    julian: true,
    full: t(format.equals, { written: marked, gregorian }),
  };
}

export interface YearDescription {
  citation: string;
  plainCitation: string;
  start: FormattedDay;
  end: FormattedDay;
  /** "1661–62" — the Gregorian calendar years the regnal year touches. */
  years: string;
}

/** The Gregorian years a span of days touches: "1661", "1661–62". */
function yearSpan(start: number, end: number): string {
  const first = fromJdn(start, "gregorian").year;
  const last = fromJdn(end, "gregorian").year;
  if (first === last) {
    return String(first);
  }
  const tail =
    last % 100 === 0 || Math.floor(first / 100) !== Math.floor(last / 100)
      ? String(last)
      : String(last % 100).padStart(2, "0");
  return `${first}–${tail}`;
}

export function describeRegnalYear(
  year: RegnalYear,
  format: DateFormat
): YearDescription {
  return {
    citation: year.label,
    plainCitation: year.plainLabel,
    start: formatLegalDay(year.start, format),
    end: formatLegalDay(year.end, format),
    years: yearSpan(year.start, year.end),
  };
}
